import React, { useState, useMemo } from 'react';
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import PaymentBadge from '../../common/PaymentBadge';
import ZoneBadge from '../../common/ZoneBadge';
import { formatCurrency, formatDate, parseDateAndTime } from '../../helpers/UtilityHelper';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFTextField } from "../../common/hook-form";
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useNdr from "../../hooks/useNdr";
import Loader from '../../common/Loader';
import { MerchantFilterCached, CourierFilter } from '../../common/ProshipFormComponents';
import PageTitle from '../../components/PageTitle';
import { subDays } from 'date-fns';
import StatusChip from '../../common/StatusChip';
import OrderRowActions from '../../common/OrderRowActions';
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);



const AwbLink = ({ awb, id }) => (
    <Box
        component={Link}
        to={`/order-detail/${awb}?id=${id}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            fontSize: '12px', fontWeight: 700, letterSpacing: '0.02em',
            color: '#1d4ed8', textDecoration: 'none',
            borderBottom: '1px dashed #93c5fd', pb: '1px',
            transition: 'color 0.15s, border-color 0.15s',
            '&:hover': { color: '#1e40af', borderBottomStyle: 'solid', borderBottomColor: '#1d4ed8' },
        }}
    >
        {awb}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{
        fontSize: '10px', fontWeight: 600, color: '#9ca3af',
        textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px',
    }}>
        {children}
    </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

function CancellationPending() {
    const methods = useForm({
        defaultValues: {
            status: "",
            courier: null,
            merchant: '',
            reference: '',
            awb: '',
            city: '',
            state: '',
            fromDate: '',
            toDate: ''
        }
    });
    const { handleSubmit, reset } = methods;

    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [reload, setReload] = useState(0);

    const request = useMemo(() => {
        const includes = "id,reference,invoiceValue,pods,shippingLabelId,awbRegisteredDate,merchantPricing.zone,merchantDetail,courierDetail,pickupDetails.from_pincode,pickupDetails.from_city,deliveryDetails.to_pincode,deliveryDetails.to_state,deliveryDetails.to_city,channelName,orderStatus,shipmentDetail,paymentMode,awbNumber,referenceNumber,merchantPricing.price";
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "orderSubStatus", operator: "eq", value: "CANCELLED_PENDING" });
        andfilter.push({ "field": "orderType", operator: "eq", value: "FORWARD SHIPMENT" });
        andfilter.push({ "field": "shipmentType", operator: "eq", value: 'B2C' });
        andfilter.push({ "field": "merchantId", operator: "ne", value: 260425082022 });

        if (filters.status) {
            andfilter.push({ "field": "orderStatus", "operator": "in", "value": [filters.status] });
        }
        if (filters.awb) {
            andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        }
        if (filters.city) {
            andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        }
        if (filters.state) {
            andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        }
        if (filters.merchant) {
            andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        }
        if (filters.courier) {
            andfilter.push({ "field": "courier.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.courier + '\')' });
        }
        if (filters.reference) {
            orFilter.push({ "field": "reference", "operator": "like", "value": filters.reference + ".*" });
            orFilter.push({ "field": "shippingLabelId", "operator": "like", "value": filters.reference + ".*" });
        }
        if (filters.fromDate) {
            andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') });
        } else {
            andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(subDays(new Date(), 60), 'yyyy-MM-dd') });
        }
        if (filters.toDate) {
            andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') });
        }
        return {
            "andfilter": andfilter,
            "orfilter": orFilter,
            "offset": (page) * sizePerPage,
            "limit": sizePerPage,
            "skipCount": true,
            "sort": { "field": "orderDate", "direction": "DESC" },
            "includes": includes
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterChange, page, sizePerPage, reload]);

    const request1 = useMemo(() => {
        const exclude = "courier,merchant,version,order_history,itemList,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,actualCourierProviderName,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,channelName,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customerDetail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchantPricing.pricingObj,merchantPricing.merchantRateCard,modifiedByUser,lastModifiedDate,terminalStatus";
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "orderSubStatus", operator: "eq", value: "CANCELLED_PENDING" });
        andfilter.push({ "field": "orderType", operator: "eq", value: "REVERSE SHIPMENT" });
        andfilter.push({ "field": "shipmentType", operator: "eq", value: 'B2C' });
        andfilter.push({ "field": "merchantId", operator: "ne", value: 260425082022 });

        if (filters.status) {
            andfilter.push({ "field": "orderStatus", "operator": "in", "value": [filters.status] });
        }
        if (filters.awb) {
            andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        }
        if (filters.city) {
            andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        }
        if (filters.state) {
            andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        }
        if (filters.merchant) {
            andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        }
        if (filters.courier) {
            andfilter.push({ "field": "courier.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.courier + '\')' });
        }
        if (filters.reference) {
            orFilter.push({ "field": "reference", "operator": "like", "value": filters.reference + ".*" });
            orFilter.push({ "field": "shippingLabelId", "operator": "like", "value": filters.reference + ".*" });
        }
        if (filters.fromDate) {
            andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') });
        } else {
            andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(subDays(new Date(), 60), 'yyyy-MM-dd') });
        }
        if (filters.toDate) {
            andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') });
        }
        return {
            "andfilter": andfilter, "orfilter": orFilter,
            "offset": (page) * sizePerPage, "limit": sizePerPage,
            "skipCount": false, "excludes": exclude
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterChange, page, sizePerPage, reload]);

    let { data = [], isFetching, isLoading } = useNdr({ request: request });
    const { data: allRows } = useNdr({ request: request1 });

    const onSubmit = (data) => {
        setFiltersChange(filterChange + 1);
        setFilters(data);
    };

    const columns = [
        {
            field: 'merchant',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1.2,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>,
        },
        {
            field: 'order_details',
            headerName: 'Order Details', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 170, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                        <Box sx={{ fontSize: '11px', color: 'text.secondary' }}>{row.shippingLabelId}</Box>
                    )}
                    {row.createdDate && (
                        <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{parseDateAndTime(row.createdDate)}</Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'statusDetails',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1,
            renderCell: ({ row }) => <StatusChip status={row.orderStatus} />,
        },
        {
            field: 'zone',
            headerName: 'Zone', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => <ZoneBadge zone={row.merchant_pricing?.zone} />,
        },
        {
            field: 'price',
            headerName: 'Price', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 110, flex: 0.8,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                    {row.merchant_pricing?.price ? formatCurrency(row.merchant_pricing.price) : '—'}
                </Box>
            ),
        },
        {
            field: 'courier',
            headerName: 'Courier & AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 190, flex: 1.4,
            renderCell: ({ row }) => {
                const courierParent = row.courierDetail?.parent?.includes('Blended')
                    ? row.actualCourierProviderName
                    : row.courierDetail?.parent;
                return (
                    <CellStack>
                        {courierParent
                            ? <CellLine bold>{courierParent}</CellLine>
                            : <CellLine muted>Unknown courier</CellLine>
                        }
                        {row.awb_number
                            ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    <AwbLink awb={row.awb_number} id={row.id} />
                                    <CopyIcon text={row.awb_number} />
                                </Box>
                            )
                            : <Box sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Box>
                        }
                        {row.courierDetail?.prozoId && (
                            <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>
                        )}
                    </CellStack>
                );
            },
        },
        {
            field: 'pickup',
            headerName: 'Pickup', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.9,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.pickup_details?.from_city && <CellLine bold>{row.pickup_details.from_city}</CellLine>}
                    {row.pickup_details?.from_pincode && (
                        <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{row.pickup_details.from_pincode}</Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'delivery',
            headerName: 'Delivery', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.1,
            renderCell: ({ row }) => {
                const cityState = row.delivery_details?.to_city && row.delivery_details?.to_state
                    ? `${row.delivery_details.to_city}, ${row.delivery_details.to_state}`
                    : null;
                return (
                    <CellStack>
                        {cityState && <CellLine bold>{cityState}</CellLine>}
                        {row.delivery_details?.to_pincode && (
                            <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{row.delivery_details.to_pincode}</Box>
                        )}
                    </CellStack>
                );
            },
        },
        {
            field: 'shipment',
            headerName: 'Package', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.1,
            renderCell: ({ row }) => {
                const s = row.shipment_detail?.[0];
                if (!s) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                const dead = (s.item_weight / 1000).toFixed(2);
                const vol = ((s.item_length * s.item_breadth * s.item_height) / 5000).toFixed(2);
                const dim = `${s.item_length}×${s.item_breadth}×${s.item_height} cm`;
                return (
                    <CellStack>
                        <Box sx={{ fontSize: '12.5px', color: 'text.primary' }}><DataLabel>Dead</DataLabel>{dead} kg</Box>
                        <Box sx={{ fontSize: '12px', color: 'text.secondary' }}><DataLabel>Vol</DataLabel>{vol} kg</Box>
                        <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{dim}</Box>
                    </CellStack>
                );
            },
        },
        {
            field: 'paymentDetails',
            headerName: 'Payment', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.9,
            renderCell: ({ row }) => (
                <CellStack>
                    <Box sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        {formatCurrency(row.invoice_value)}
                    </Box>
                    <PaymentBadge mode={row.payment_mode} />
                </CellStack>
            ),
        },
        {
            field: 'action',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => (
                <OrderRowActions
                    actions={[
                        {
                            key: 'view',
                            label: 'View details',
                            icon: <VisibilityIcon fontSize="small" />,
                            href: `/order-detail/${row.awb_number}?id=${row.id}`,
                            target: '_blank',
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle>Cancellation Pending Shipments</PageTitle>
            {(isFetching || isLoading) && <Loader />}

            {/* ── Filter bar ────────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={"B2C"} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Order Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name="city" label="" placeholder="City" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name="state" label="" placeholder="State" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Button type="submit" variant="contained" size="small"
                                    sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Button>
                                <Button type="button" variant="contained" size="small"
                                    sx={{ bgcolor: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#dc2626' } }}
                                    onClick={() => {
                                        reset({ status: "", courier: '', merchant: '', reference: '', awb: '', city: '', state: '', fromDate: null, toDate: null });
                                        setFilters('');
                                        setReload(() => reload + 1);
                                    }}>
                                    Reset
                                </Button>
                            </Box>

                        </Box>
                    </FormProvider>

                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>
                            By default, only records from the past 60 days are shown.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={(data?.data && data?.data.length > 0) ? data?.data : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    rowCount={allRows?.count || 0}
                    getRowId={(row) => row.id}
                />
            </Box>
        </>
    );
}

export default CancellationPending;
